import { all, call, put, select, takeEvery, takeLatest } from "redux-saga/effects";
import { SteeringDataAPI, Request } from "@ai360/core";
import { actions as notificationActions, MSGTYPE } from "~/notifications";
import { actions as messagingActions } from "~/messaging";
import { getTheUserCompanyGuid } from "~/login/selectors";

import * as actions from "./actions";
import { getLoadedChildParentGuids, getSteeringDataScreenActive } from "./selectors";

const STEERING_DATA_STATUS_EVENT = "steeringDataStatus";
const STEERING_DATA_LIST_CHANGED_EVENT = "steeringDataListChanged";

// --- Fetch List ---

export function* fetchSteeringDataListSaga(action) {
    const silent = action?.payload?.silent === true;
    try {
        if (!silent) {
            yield put(actions.setSteeringDataLoading(true));
        }
        const ownerGuid = yield select(getTheUserCompanyGuid);
        const response = yield call(SteeringDataAPI.getSteeringDataList, ownerGuid);
        yield put(actions.fetchSteeringDataListSuccess(response));
    } catch (error) {
        yield put(
            actions.fetchSteeringDataListFailed(
                error?.message || "Failed to fetch steering data list"
            )
        );
        yield put(notificationActions.apiCallError(error, action));
    }
}

// --- Fetch Single Record ---

export function* fetchSteeringDataRecordSaga(action) {
    const { payload } = action;
    try {
        yield put(actions.setSteeringDataLoading(true));
        const response = yield call(SteeringDataAPI.getSteeringDataRecord, payload);
        yield put(actions.fetchSteeringDataRecordSuccess(response));
    } catch (error) {
        yield put(
            actions.fetchSteeringDataRecordFailed(
                error?.message || "Failed to fetch steering data record"
            )
        );
        yield put(notificationActions.apiCallError(error, action));
    }
}

// --- Create ---
// Handles file upload flow: create record → get pre-signed URL → upload file

export function* createSteeringDataSaga(action) {
    const { payload } = action;
    const { file, ...createRequest } = payload;
    try {
        yield put(actions.setSteeringDataLoading(true));

        // Ensure ownerGuid is included in the request body (required by the RESTful API)
        const ownerGuid = yield select(getTheUserCompanyGuid);
        const requestWithOwner = { ...createRequest, ownerGuid };

        // Step 1: Create the steering data record
        const record = yield call(SteeringDataAPI.createSteeringData, requestWithOwner);

        // Step 2: If a file was provided, upload it via pre-signed URL
        if (file) {
            // Get pre-signed URL from backend
            const presignedResponse = yield call(
                SteeringDataAPI.getUploadPresignedUrl,
                record.steeringDataGuid,
                file.name,
                ownerGuid
            );

            // Upload file directly to S3 using the pre-signed URL
            yield call(Request.uploadToS3, presignedResponse.url, file, "application/octet-stream");

            // Trigger backend file processing (text extraction + embedding)
            try {
                yield call(SteeringDataAPI.processFile, record.steeringDataGuid);
            } catch (processError) {
                // Non-fatal: file is uploaded, processing will occur on next deploy
                console.warn("process-file trigger failed (non-blocking):", processError);
            }
        }

        // Step 3: Dispatch success
        yield put(actions.createSteeringDataSuccess(record));
        yield put(
            notificationActions.pushToasterMessage(
                "Steering data record created successfully.",
                MSGTYPE.SUCCESS
            )
        );

        // Refresh the list
        yield put(actions.fetchSteeringDataList());
    } catch (error) {
        yield put(
            actions.createSteeringDataFailed(error?.message || "Failed to create steering data")
        );
        yield put(notificationActions.apiCallError(error, action));
    }
}

// --- Create Batch ---

// Uploads each file as a CHILD of an existing parent: create child → pre-signed
// URL → upload → trigger processing (the same three steps a single file record
// uses). Returns { succeeded, failedNames }. Shared by the create-batch flow
// (new parent) and the add-children flow (existing parent).
function* uploadFilesAsChildren(parentGuid, files, hierarchyLevelGuids, ownerGuid) {
    let succeeded = 0;
    const failedNames = [];

    for (const file of files) {
        try {
            const child = yield call(SteeringDataAPI.createSteeringDataChild, parentGuid, {
                name: file.name,
                hierarchyLevelGuids,
            });

            const presignedResponse = yield call(
                SteeringDataAPI.getUploadPresignedUrl,
                child.steeringDataGuid,
                file.name,
                ownerGuid
            );
            yield call(Request.uploadToS3, presignedResponse.url, file, "application/octet-stream");

            try {
                yield call(SteeringDataAPI.processFile, child.steeringDataGuid);
            } catch (processError) {
                console.warn("process-file trigger failed (non-blocking):", processError);
            }

            succeeded += 1;
        } catch (fileError) {
            console.warn(`Failed to create child record for ${file.name}:`, fileError);
            failedNames.push(file.name);
        }
    }

    return { succeeded, failedNames };
}

// Multi-file manual upload: create ONE parent record (no file of its own) and
// attach each uploaded file as a CHILD record — exactly like the url-files
// workflow. The grid shows the single parent; the files appear in its sublist.
export function* createSteeringDataBatchSaga(action) {
    const { payload } = action;
    const { files, baseName, hierarchyLevelGuids, notes } = payload;

    try {
        yield put(actions.setSteeringDataLoading(true));

        const ownerGuid = yield select(getTheUserCompanyGuid);

        // Step 1: create the single parent (a file-type container with no file of
        // its own). Its children carry the data; C# keeps the parent Complete.
        const parent = yield call(SteeringDataAPI.createSteeringData, {
            name: baseName,
            sourceType: "file",
            ownerGuid,
            hierarchyLevelGuids,
            notes,
        });

        // Step 2: upload each file as a child under the parent.
        const { succeeded, failedNames } = yield call(
            uploadFilesAsChildren,
            parent.steeringDataGuid,
            files,
            hierarchyLevelGuids,
            ownerGuid
        );

        yield put(actions.createSteeringDataBatchSuccess({ succeeded, failed: failedNames }));

        if (failedNames.length === 0) {
            yield put(
                notificationActions.pushToasterMessage(
                    `Steering data record created with ${succeeded} file${
                        succeeded === 1 ? "" : "s"
                    }.`,
                    MSGTYPE.SUCCESS
                )
            );
        } else {
            yield put(
                notificationActions.pushToasterMessage(
                    `${succeeded} file(s) added, ${failedNames.length} failed: ${failedNames.join(
                        ", "
                    )}`,
                    MSGTYPE.WARNING
                )
            );
        }

        // Refresh the list once
        yield put(actions.fetchSteeringDataList());
    } catch (error) {
        yield put(
            actions.createSteeringDataBatchFailed(
                error?.message || "Failed to create steering data records"
            )
        );
        yield put(notificationActions.apiCallError(error, action));
    }
}

// Add more files as children of an existing container parent (edit mode). Same
// per-file child flow as create-batch, but the parent already exists.
export function* addSteeringDataChildrenSaga(action) {
    const { payload } = action;
    const { parentGuid, files, hierarchyLevelGuids } = payload;

    try {
        yield put(actions.setSteeringDataLoading(true));

        const ownerGuid = yield select(getTheUserCompanyGuid);

        const { succeeded, failedNames } = yield call(
            uploadFilesAsChildren,
            parentGuid,
            files,
            hierarchyLevelGuids,
            ownerGuid
        );

        yield put(actions.addSteeringDataChildrenSuccess({ parentGuid, succeeded }));

        if (failedNames.length === 0) {
            yield put(
                notificationActions.pushToasterMessage(
                    `${succeeded} file${succeeded === 1 ? "" : "s"} added.`,
                    MSGTYPE.SUCCESS
                )
            );
        } else {
            yield put(
                notificationActions.pushToasterMessage(
                    `${succeeded} file(s) added, ${failedNames.length} failed: ${failedNames.join(
                        ", "
                    )}`,
                    MSGTYPE.WARNING
                )
            );
        }

        // Refresh the parent's sublist and the list.
        yield put(actions.fetchSteeringDataChildren(parentGuid));
        yield put(actions.fetchSteeringDataList());
    } catch (error) {
        yield put(
            actions.addSteeringDataChildrenFailed(
                error?.message || "Failed to add files to steering data record"
            )
        );
        yield put(notificationActions.apiCallError(error, action));
    }
}

// --- Update ---

export function* updateSteeringDataSaga(action) {
    const { payload } = action;
    const { steeringDataGuid, file, ...updateRequest } = payload;
    try {
        yield put(actions.setSteeringDataLoading(true));
        const ownerGuid = yield select(getTheUserCompanyGuid);

        // Step 1: Update the steering data record
        const record = yield call(
            SteeringDataAPI.updateSteeringData,
            steeringDataGuid,
            updateRequest
        );

        // Step 2: If a new file was provided, upload it via pre-signed URL
        if (file) {
            const presignedResponse = yield call(
                SteeringDataAPI.getUploadPresignedUrl,
                steeringDataGuid,
                file.name,
                ownerGuid
            );

            yield call(Request.uploadToS3, presignedResponse.url, file, "application/octet-stream");

            // Trigger backend file processing (text extraction + embedding)
            try {
                yield call(SteeringDataAPI.processFile, steeringDataGuid);
            } catch (processError) {
                // Non-fatal: file is uploaded, processing will occur on next deploy
                console.warn("process-file trigger failed (non-blocking):", processError);
            }
        }

        // Step 3: Dispatch success
        yield put(actions.updateSteeringDataSuccess(record));
        yield put(
            notificationActions.pushToasterMessage(
                "Steering data record updated successfully.",
                MSGTYPE.SUCCESS
            )
        );

        // Refresh the list
        yield put(actions.fetchSteeringDataList());
    } catch (error) {
        yield put(
            actions.updateSteeringDataFailed(error?.message || "Failed to update steering data")
        );
        yield put(notificationActions.apiCallError(error, action));
    }
}

// --- Delete ---

export function* deleteSteeringDataSaga(action) {
    const { payload } = action;
    try {
        yield put(actions.setSteeringDataLoading(true));

        yield call(SteeringDataAPI.deleteSteeringData, payload);

        yield put(actions.deleteSteeringDataSuccess(payload));
        yield put(
            notificationActions.pushToasterMessage(
                "Steering data record deleted successfully.",
                MSGTYPE.SUCCESS
            )
        );

        // Refresh the list
        yield put(actions.fetchSteeringDataList());
    } catch (error) {
        yield put(
            actions.deleteSteeringDataFailed(error?.message || "Failed to delete steering data")
        );
        yield put(notificationActions.apiCallError(error, action));
    }
}

// --- Children (per-file rows of a container parent) ---

export function* fetchSteeringDataChildrenSaga(action) {
    const parentGuid = action.payload;
    try {
        const children = yield call(SteeringDataAPI.getSteeringDataChildren, parentGuid);
        yield put(actions.fetchSteeringDataChildrenSuccess({ parentGuid, children }));
    } catch (error) {
        yield put(
            actions.fetchSteeringDataChildrenFailed({
                parentGuid,
                error: error?.message || "Failed to fetch files",
            })
        );
        yield put(notificationActions.apiCallError(error, action));
    }
}

// After a list refetch, refresh any open Files sublists so newly-crawled files
// appear without a manual reopen.
export function* refreshLoadedChildrenSaga() {
    const active = yield select(getSteeringDataScreenActive);
    if (!active) {
        return;
    }
    const parentGuids = yield select(getLoadedChildParentGuids);
    for (const parentGuid of parentGuids) {
        yield put(actions.fetchSteeringDataChildren(parentGuid));
    }
}

// Refetches the list on a list-changed signal, but only while the screen is open
// (the worker already throttles how often it emits this event).
export function* handleListChangedSignalSaga() {
    const active = yield select(getSteeringDataScreenActive);
    if (!active) {
        return;
    }
    yield put(actions.fetchSteeringDataList({ silent: true }));
}

export function* deleteSteeringDataChildSaga(action) {
    const { parentGuid, childGuid } = action.payload;
    try {
        yield call(SteeringDataAPI.deleteSteeringDataChild, parentGuid, childGuid);
        yield put(actions.deleteSteeringDataChildSuccess({ parentGuid, childGuid }));
        yield put(
            notificationActions.pushToasterMessage("File deleted successfully.", MSGTYPE.SUCCESS)
        );
    } catch (error) {
        yield put(
            actions.deleteSteeringDataChildFailed({
                parentGuid,
                error: error?.message || "Failed to delete file",
            })
        );
        yield put(notificationActions.apiCallError(error, action));
    }
}

export function* deleteSteeringDataChildrenSaga(action) {
    const { parentGuid, childGuids } = action.payload;
    try {
        yield call(SteeringDataAPI.deleteSteeringDataChildren, parentGuid, childGuids);
        yield put(actions.deleteSteeringDataChildrenSuccess({ parentGuid, childGuids }));
        yield put(
            notificationActions.pushToasterMessage(
                `${childGuids.length} file${
                    childGuids.length === 1 ? "" : "s"
                } deleted successfully.`,
                MSGTYPE.SUCCESS
            )
        );
    } catch (error) {
        yield put(
            actions.deleteSteeringDataChildrenFailed({
                parentGuid,
                error: error?.message || "Failed to delete files",
            })
        );
        yield put(notificationActions.apiCallError(error, action));
    }
}

// --- Real-time status updates (IoT messaging) ---

// Subscribes to the "steeringDataStatus" event. Incoming model is
// { steeringDataGuid, embeddingStatus, chunkCount?, embeddingError? }; it is
// dispatched straight to updateSteeringDataStatus to update the row in place.
export function* subscribeSteeringDataStatus() {
    yield put(
        messagingActions.subscribe(0, {
            eventName: STEERING_DATA_STATUS_EVENT,
            action: (model) => actions.updateSteeringDataStatus(model),
        })
    );
}

// Subscribes to the "steeringDataListChanged" event, emitting a signal that the
// debounced, screen-gated saga turns into a silent list refetch.
export function* subscribeSteeringDataListChanged() {
    yield put(
        messagingActions.subscribe(0, {
            eventName: STEERING_DATA_LIST_CHANGED_EVENT,
            action: () => actions.steeringDataListChangedSignal(),
        })
    );
}

// --- Root Saga ---

export function* steeringDataSagas() {
    yield all([
        subscribeSteeringDataStatus(),
        subscribeSteeringDataListChanged(),
        takeLatest(actions.fetchSteeringDataList, fetchSteeringDataListSaga),
        takeLatest(actions.fetchSteeringDataRecord, fetchSteeringDataRecordSaga),
        takeLatest(actions.createSteeringData, createSteeringDataSaga),
        takeLatest(actions.createSteeringDataBatch, createSteeringDataBatchSaga),
        takeEvery(actions.addSteeringDataChildren, addSteeringDataChildrenSaga),
        takeLatest(actions.updateSteeringData, updateSteeringDataSaga),
        takeLatest(actions.deleteSteeringData, deleteSteeringDataSaga),
        takeEvery(actions.fetchSteeringDataChildren, fetchSteeringDataChildrenSaga),
        takeEvery(actions.deleteSteeringDataChild, deleteSteeringDataChildSaga),
        takeEvery(actions.deleteSteeringDataChildren, deleteSteeringDataChildrenSaga),
        takeLatest(actions.fetchSteeringDataListSuccess, refreshLoadedChildrenSaga),
        takeLatest(actions.steeringDataListChangedSignal, handleListChangedSignalSaga),
    ]);
}

export default steeringDataSagas;
